// frontend/src/pages/Resumes/index.tsx

import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { ResumeList } from '@/components/resume/ResumeCard'
import { ResumeForm } from '@/components/resume/ResumeForm'
import { SearchBar } from '@/components/search/SearchBar'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Select } from '@/components/common/UI/Select'
import { Card } from '@/components/common/UI/Card'
import { Spinner } from '@/components/common/UI/Spinner'
import { Modal, useModal } from '@/components/common/UI/Modal'
import { useToast } from '@/components/common/UI/Toast'
import { useAuthStore } from '@/store'

const Resumes: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [filters, setFilters] = useState({
    city: '',
    experience_level: '',
    skills: '',
  })
  const [page, setPage] = useState(1)
  const [blacklistCompanyId, setBlacklistCompanyId] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const createModal = useModal()

  const isEmployer = user?.role === 'employer' || user?.role === 'admin'
  const isCandidate = user?.role === 'candidate'

  // Получение резюме
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['resumes', searchQuery, filters, page],
    queryFn: async () => {
      const params: any = {
        page,
        limit: 20,
      }

      if (searchQuery) {
        params.search = searchQuery
      }
      if (filters.city) {
        params.city = filters.city
      }
      if (filters.experience_level) {
        params.experience_level = filters.experience_level
      }
      if (filters.skills) {
        params.skills = filters.skills.split(',').map(s => s.trim())
      }

      // Если работодатель - ищем публичные резюме
      // Если кандидат - свои резюме
      if (isCandidate) {
        const response = await apiClient.get<any>('/v1/resumes/my', { params })
        return Array.isArray(response) ? { items: response, total: response.length } : response
      }
      const response = await apiClient.get<any>('/v1/resumes', { params })
      return Array.isArray(response) ? { items: response, total: response.length } : response
    },
    enabled: isAuthenticated && (isEmployer || isCandidate)
  })

  const { data: resumeProgress } = useQuery({
    queryKey: ['resumes', 'my', 'progress'],
    queryFn: () => apiClient.get('/v1/resumes/my/progress'),
    enabled: isCandidate,
  })

  const { data: blacklist = [], refetch: refreshBlacklist } = useQuery({
    queryKey: ['companies', 'blacklist'],
    queryFn: () => apiClient.get('/v1/resumes/blacklist'),
    enabled: isCandidate,
  })
  const { data: companiesResponse } = useQuery({
    queryKey: ['companies', 'blacklist-options'],
    queryFn: () => apiClient.get('/v1/companies', { params: { limit: 100 } }),
    enabled: isCandidate,
  })
  const blacklistCompanies = Array.isArray(companiesResponse) ? companiesResponse : companiesResponse?.items || []
  const addBlacklistMutation = useMutation({
    mutationFn: (companyId: string) => apiClient.post(`/v1/resumes/blacklist/${companyId}`),
    onSuccess: () => { setBlacklistCompanyId(''); refreshBlacklist() },
  })
  const removeBlacklistMutation = useMutation({
    mutationFn: (companyId: number) => apiClient.delete(`/v1/resumes/blacklist/${companyId}`),
    onSuccess: () => refreshBlacklist(),
  })

  const resumes = data?.items || []
  const total = data?.total || 0

  // Удаление резюме
  const deleteMutation = useMutation({
    mutationFn: async (resumeId: number) => {
      await apiClient.delete(`/v1/resumes/${resumeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      addToast('Резюме удалено', 'success')
    },
    onError: (error: any) => {
      addToast(
        error.response?.data?.detail || 'Ошибка удаления резюме',
        'error'
      )
    }
  })

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setSearchParams({ q: query })
    setPage(1)
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const handleResetFilters = () => {
    setFilters({
      city: '',
      experience_level: '',
      skills: '',
    })
    setPage(1)
  }

  const handleDelete = (resumeId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить это резюме?')) {
      deleteMutation.mutate(resumeId)
    }
  }

  // Проверка прав доступа
  if (!isAuthenticated) {
    return (
      <div className="container-custom py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Доступ к резюме</h1>
        <p className="text-secondary-600 mb-6">
          {isEmployer ? 'Войдите, чтобы просматривать резюме кандидатов' : 'Войдите, чтобы управлять своими резюме'}
        </p>
        <Button onClick={() => window.location.href = '/login'}>
          Войти
        </Button>
      </div>
    )
  }

  if (!isEmployer && !isCandidate) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-secondary-500">У вас нет доступа к этому разделу</p>
      </div>
    )
  }

  return (
    <div className="container-custom py-8">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            {isCandidate ? 'Мои резюме' : 'База резюме'}
          </h1>
          {total > 0 && (
            <p className="text-sm text-secondary-500">Найдено: {total} резюме</p>
          )}
        </div>

        {isCandidate && (
          <Button onClick={createModal.open}>
            + Создать резюме
          </Button>
        )}
      </div>

      {isCandidate && resumeProgress?.resumes?.length > 0 && (
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium">Заполнение самого полного резюме</p>
            <span className="text-sm font-semibold text-primary-700">{resumeProgress.overall_percentage}%</span>
          </div>
          <div className="h-2 rounded-full bg-secondary-100 overflow-hidden">
            <div className="h-full bg-primary-600 transition-all" style={{ width: `${resumeProgress.overall_percentage}%` }} />
          </div>
          {resumeProgress.resumes.some((item: any) => item.missing.length > 0) && (
            <p className="text-xs text-secondary-500 mt-2">Заполните навыки, опыт и образование, чтобы работодателям было проще оценить профиль.</p>
          )}
        </Card>
      )}

      {isCandidate && (
        <Card className="p-4 mb-6">
          <h2 className="font-semibold mb-3">Компании, которым не показывать резюме</h2>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <select value={blacklistCompanyId} onChange={(event) => setBlacklistCompanyId(event.target.value)} className="flex-1 rounded-md border border-secondary-300 px-3 py-2">
              <option value="">Выберите компанию</option>
              {blacklistCompanies.filter((company: any) => !blacklist.some((entry: any) => entry.company_id === company.id)).map((company: any) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
            <Button disabled={!blacklistCompanyId || addBlacklistMutation.isPending} onClick={() => addBlacklistMutation.mutate(blacklistCompanyId)}>Добавить в список</Button>
          </div>
          {blacklist.length === 0 ? <p className="text-sm text-secondary-500">Список пока пуст.</p> : (
            <ul className="divide-y divide-secondary-100">
              {blacklist.map((entry: any) => <li key={entry.company_id} className="flex items-center justify-between py-2 text-sm">
                <span>{entry.company_name}</span>
                <button className="text-error hover:underline" onClick={() => removeBlacklistMutation.mutate(entry.company_id)}>Убрать</button>
              </li>)}
            </ul>
          )}
        </Card>
      )}

      {/* Поиск и фильтры */}
      <div className="space-y-4 mb-6">
        <SearchBar
          placeholder={
            isCandidate
              ? 'Поиск среди моих резюме...'
              : 'Поиск кандидатов по навыкам, позиции...'
          }
          onSearch={handleSearch}
          initialValue={searchQuery}
        />

        <button
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
        </button>

        {showFilters && (
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Город"
                placeholder="Москва"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />

              <Select
                label="Опыт работы"
                options={[
                  { value: '', label: 'Любой' },
                  { value: 'no_experience', label: 'Без опыта' },
                  { value: 'less_1_year', label: 'Менее 1 года' },
                  { value: 'from_1_to_3', label: '1-3 года' },
                  { value: 'from_3_to_6', label: '3-6 лет' },
                  { value: 'more_6', label: 'Более 6 лет' },
                ]}
                value={filters.experience_level}
                onChange={(value: string) => handleFilterChange('experience_level', value)}
              />

              <Input
                label="Навыки (через запятую)"
                placeholder="Python, React, SQL"
                value={filters.skills}
                onChange={(e) => handleFilterChange('skills', e.target.value)}
              />
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                Сбросить фильтры
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Результаты */}
      {isLoading && page === 1 ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-error">Ошибка загрузки резюме</p>
          <Button variant="outline" className="mt-2" onClick={() => refetch()}>
            Попробовать снова
          </Button>
        </div>
      ) : (
        <>
          <ResumeList
            resumes={resumes}
            showScore={isEmployer}
            {...(isCandidate ? { onDelete: handleDelete, onEdit: (resume: any) => {
              // Открыть форму редактирования
              window.location.href = `/resumes/${resume.id}/edit`
            } } : {})}
            emptyMessage={
              isCandidate
                ? 'У вас пока нет резюме. Создайте первое резюме!'
                : 'Резюме не найдены. Попробуйте изменить параметры поиска.'
            }
          />

          {/* Пагинация */}
          {resumes.length > 0 && resumes.length < total && (
            <div className="text-center mt-6">
              <Button
                variant="outline"
                onClick={() => setPage(page + 1)}
                isLoading={isLoading}
              >
                Загрузить еще
              </Button>
            </div>
          )}
        </>
      )}

      {/* Модалка создания резюме */}
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        title="Создание резюме"
        size="lg"
      >
        <ResumeForm
          onSuccess={() => {
            createModal.close()
            queryClient.invalidateQueries({ queryKey: ['resumes'] })
            addToast('Резюме создано', 'success')
          }}
          onCancel={createModal.close}
        />
      </Modal>
    </div>
  )
}

export default Resumes
